// VacationApprovalModal.jsx
import React, { useState, useEffect } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    CircularProgress,
    TextField,
} from "@mui/material";
import { useService } from 'contexts/ServiceContextProvider';
import SubjectIcon from '@mui/icons-material/Subject';
import WarningIcon from '@mui/icons-material/Warning';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useTranslation } from "react-i18next";
import "../../css/components/Calendar/VacationApprovalModal.css";
import "../../css/components/Calendar/VacationCalendar.css";
import "../../css/components/Calendar/VacationRequestModal.css";
import "../../css/main.css";
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { calculateWeekDaysForVacation, extractCountryCode } from './calculateVacationDays.js'
import { DateTime } from 'luxon'
import ScrollAwareTooltip from "components/Custom/ScrollAwareTooltip";

const VacationApprovalModal = ({ open, handleCloseApproveVacationsModal, workspace_id }) => {
    const { t } = useTranslation();

    const [pendingVacations, setPendingVacations] = useState([]);
    const [selectedVacations, setSelectedVacations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const { GetPendingVacationRequests, UpdatePendingVacationRequest, getUserRemainingWorkspaceVacationDays } = useService();
    const [tooltipTitles, setTooltipTitles] = useState({});
    const [exceedsLimit, setExceedsLimit] = useState({});

    const [openModal, setOpenModal] = useState(false);
    const [currentEventId, setCurrentEventId] = useState(null);
    const [changeReason, setChangeReason] = useState('');
    const [warningTooltip, setWarningTooltip] = useState('');

    // Removed blacklistedHolidays state
    const [, setHolidays] = useState([]); // Only normal holidays

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
    if (!pendingVacations?.length) return;

    pendingVacations.forEach(vacation => {
      handleTooltipOpen(
        workspace_id,
        vacation.user.id,
        vacation.id,
        vacation.vacationDays
      );
    });
  }, [pendingVacations, workspace_id]);

    useEffect(() => {
        if (open) {
            fetchPendingVacations();
        }
    }, [open]);

    const fetchPendingVacations = async () => {
        setLoading(true);
        try {
            const data = await GetPendingVacationRequests(workspace_id);
            const { vacations, holidays: fetchedHolidays } = data;



            // Set only normal holidays
            setHolidays(fetchedHolidays);

            // Process vacations to calculate vacationDays and store overlappingHolidays
            const processedVacations = vacations.map(vacation => {
                const overlappingHolidays = fetchedHolidays.filter(holiday => {
                    const userCountryCode = extractCountryCode(vacation.user.location);
            
                    // Include custom holidays or holidays matching the user's country
                    if (holiday.source === 'Custom' || (userCountryCode && holiday.country === userCountryCode)) {
                        const holidayDate = DateTime.fromISO(holiday.date, { zone: 'utc' }).startOf('day');
                        const startDate = DateTime.fromISO(vacation.date_start, { zone: 'utc' }).startOf('day');
                        const endDate = DateTime.fromISO(vacation.date_end, { zone: 'utc' }).startOf('day');
            
                        // Ensure the holiday falls within the vacation date range
                        return holidayDate >= startDate && holidayDate <= endDate;
                    }
            
                    return false;
                });
            
                const vacationDays = calculateWeekDaysForVacation(vacation.date_start, vacation.date_end, overlappingHolidays);
            
                return { ...vacation, vacationDays, overlappingHolidays };
            });
            

            setPendingVacations(processedVacations);
        } catch (error) {
            console.error("Error fetching pending vacations and holidays:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = (vacationId) => {
        setSelectedVacations((prevSelected) => {
            if (prevSelected.includes(vacationId)) {
                return prevSelected.filter((id) => id !== vacationId);
            } else {
                return [...prevSelected, vacationId];
            }
        });
    };

    const handleAction = async (action) => {
        try {
            await UpdatePendingVacationRequest(workspace_id, selectedVacations, action);
            fetchPendingVacations();
            setSelectedVacations([]);
        } catch (error) {
            console.error(`Error ${action}ing vacations:`, error);
        }
    };

    const handleOpenRefuteModal = (eventId) => {
        setCurrentEventId(eventId);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setChangeReason('');
    };

    const handleSuggestChanges = async () => {
        try {
            await UpdatePendingVacationRequest(workspace_id, [currentEventId], 'request_changes', changeReason);
            fetchPendingVacations();
            handleCloseModal();
        } catch (error) {
            console.error('Error suggesting changes:', error);
        }
    };

    const filteredVacations = pendingVacations?.filter((vacation) =>
        vacation.user.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

    const getFullVacationTypeName = (type) => {
        switch (type) {
            case "RV":
                return t("calendar.vacation_type1");
            case "SL":
                return t("calendar.vacation_type2");
            case "PL":
                return t("calendar.vacation_type3");
            case "MPL":
                return t("calendar.vacation_type4");
            default:
                return t("calendar.vacation_type5");
        }
    };

    const handleTooltipOpen = async (workspace_id, user_id, vacation_id, requestedDays) => {
        const response = await getUserRemainingWorkspaceVacationDays(workspace_id, user_id);
        if (response && response.data) {
            const vacationDays = response.data;

            const limitExceeded = requestedDays > vacationDays.total_available_days;
            setExceedsLimit(prev => ({ ...prev, [vacation_id]: limitExceeded }));

            setTooltipTitles((prevTitles) => ({
                ...prevTitles,
                [vacation_id]: (
                    <Box className="vacation-approval-calendar-tooltip" >
                        <Typography variant="body1" component="div" className="vacation-approval-user-info">
                            <strong>{t("calendar.vacation_user_info")}</strong>
                        </Typography>
                        <Box>
                            <Typography variant="body2" component="div" className="request-vacation-type-title">
                                {t("calendar.vacation_per_year", { count: vacationDays.remaining_vacation_days })}
                            </Typography>
                            <Typography variant="body2" component="div" className="request-vacation-type-title">
                                {t("calendar.vacation_rollovers", { count: vacationDays.rollover_days })}
                            </Typography>
                            <Typography variant="body2" component="div" className="request-vacation-type-title">
                                {t("calendar.vacation_used_days", { count: vacationDays.used_vacation_days })}
                            </Typography>
                            <Typography variant="body2" component="div" className="request-vacation-type-title">
                                {t("calendar.vacation_total_days", { count: vacationDays.total_available_days })}
                            </Typography>
                            {limitExceeded && (
                            <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 'bold' }}>
                            {t("calendar.vacation_days_exceed_limit")}
                            </Typography>
                                )}
                        </Box>
                    </Box>
                ),
            }));
        } else {
            setTooltipTitles((prevTitles) => ({
                ...prevTitles,
                [vacation_id]: 'Error fetching remaining vacation days',
            }));
        }
    };

    const handleWarningTooltipOpen = () => {
        setWarningTooltip(
            <Box className="vacation-approval-calendar-tooltip">
                <Typography variant="body1" component="div" className="vacation-approval-user-info">
                    <strong>{t("calendar.vacation_warning")}</strong>
                </Typography>
                <Typography variant="body2" component="div" className="request-vacation-type-title">
                    {t("calendar.vacation_warning_info")}
                </Typography>
            </Box>
        );
    }

    function formatDateToDDMMYYYY(dateString) {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
    }

    return (
        <>
            <Modal
                open={open}
                onClose={handleCloseApproveVacationsModal}
                aria-labelledby="vacation-approval-modal-title"
                aria-describedby="vacation-approval-modal-description"
            >
                <Box className="vacation-approval-main-style">
                    <Typography
                        id="vacation-approval-modal-title"
                        variant="h6"
                        component="h2"
                        className="white-txt-color"
                    >
                        {t("calendar.vacation_pending")}
                    </Typography>
                    <TextField
                        label={t("calendar.search_users_by_name")}
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="request-vacation-type-select"
                        InputLabelProps={{ className: "request-vacation-type-title" }}
                        InputProps={{
                            className: "request-vacation-type-select",
                        }}
                    />
                    {loading ? (
                        <Box className="vacation-approval-loading">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box className="vacation-approval-requests-box">
                            <List>
                                {filteredVacations?.map((vacation) => {
                                    const vacationTypeName = getFullVacationTypeName(vacation.type);
                                    const visibilityStatus = vacation.visibility
                                        ? t("global.public")
                                        : t("global.private");
                                    const vacationDescription = vacation.description;

                                    return (
                                        <ListItem
                                            key={vacation.id}
                                            disableGutters
                                            className={`vacation-approval-vacation-request ${vacation.do_not_deduct ? 'warning' : ''}`}
                                        >
                                            <Box className="vacation-approval-flex-container">
                                                <Box className="vacation-approval-details">
                                                    <Checkbox
                                                        edge="start"
                                                        checked={selectedVacations.includes(vacation.id)}
                                                        onChange={() => handleToggle(vacation.id)}
                                                        className="white-txt-color"
                                                    />
                                                    <ListItemText
                                                        primary={
                                                            <span>
                                                                <Typography className="white-txt-color" variant="subtitle1">
                                                                    {vacation.title}
                                                                </Typography>
                                                                <span className="vacation-approval-date-color">
                                                                    ({formatDateToDDMMYYYY(vacation.date_start)} {t("calendar.vacation_to")} {formatDateToDDMMYYYY(vacation.date_end)})
                                                                </span>
                                                            </span>
                                                        }
                                                        secondary={
                                                            <Box className="vacation-approval-description">
                                                                {vacationDescription && (
                                                                    <ScrollAwareTooltip title={vacationDescription} arrow>
                                                                        <SubjectIcon className="vacation-approval-description-icon" />
                                                                    </ScrollAwareTooltip>
                                                                )}
                                                                <Typography>{vacationTypeName} -</Typography>
                                                                <Typography
                                                                    className={`vacation-approval-visibility ${visibilityStatus === 'Public' ? 'vacation-approval-visibility-public' : 'vacation-approval-visibility-private'
                                                                        }`}
                                                                >
                                                                    {visibilityStatus}
                                                                </Typography>
                                                                <ScrollAwareTooltip
                                                                    title={tooltipTitles[vacation.id]}
                                                                    arrow
                                                                    onOpen={() =>
                                                                        handleTooltipOpen(
                                                                            workspace_id,
                                                                            vacation.user.id,
                                                                            vacation.id,
                                                                            vacation.vacationDays
                                                                        )
                                                                    }
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display: "inline-flex",
                                                                            alignItems: "center",
                                                                            ml: 1,
                                                                            cursor: "pointer",
                                                                        }}
                                                                    >
                                                                        {exceedsLimit[vacation.id] && (
                                                                            <WarningIcon
                                                                                color="error"
                                                                                sx={{ fontSize: 18, mr: 0.5 }}
                                                                                titleAccess={t("calendar.vacation_exceeds_balance")}
                                                                                aria-label="Exceeds available balance"
                                                                            />
                                                                        )}

                                                                        <CalendarTodayIcon className="vacation-approval-calendar-icon" />
                                                                    </Box>
                                                                </ScrollAwareTooltip>
                                                                {vacation.do_not_deduct && (
                                                                    <ScrollAwareTooltip
                                                                        title={warningTooltip}
                                                                        arrow
                                                                        onOpen={handleWarningTooltipOpen}
                                                                    >
                                                                        <WarningIcon className="vacation-approval-warning-icon" />
                                                                    </ScrollAwareTooltip>
                                                                )}
   
                                                                {/* Display Overlapping Holidays */}
                                                                {vacation.overlappingHolidays.length > 0 && (
                                                                    <Box className="overlapping-holidays" style={{ marginTop: '8px'}}>
                                                                        <Typography variant="body2" component="div" className="request-vacation-type-title">
                                                                            <strong style={{ color: 'lightblue' }}>
                                                                                {t("calendar.holiday_overlap", { count: vacation.overlappingHolidays.length })}
                                                                            </strong>
                                                                        </Typography>
                                                                        {vacation.overlappingHolidays.map(holiday => (
                                                                            <Box key={holiday.id} className="overlapping-holiday-item" style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                                                                <Typography variant="body2">
                                                                                    - {holiday.name} ({formatDateToDDMMYYYY(holiday.date)})
                                                                                </Typography>
                                                                                <img
                                                                                    src={`https://flagcdn.com/w80/${extractCountryCode(vacation.user.location).toLowerCase()}.png`}
                                                                                    alt={`${extractCountryCode(vacation.user.location)} Flag`}
                                                                                    style={{ width: 20, height: 14, marginLeft: 4 }}
                                                                                />
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        }
                                                    />
                                                </Box>
                                                <Box className="vacation-approval-actions">
                                                    <Button
                                                        variant="contained"
                                                        className="vacation-approval-refute"
                                                        onClick={() => handleOpenRefuteModal(vacation.id)}
                                                    >
                                                        {t("calendar.vacation_refute_button")}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Box>
                    )}
                    <Box className="vacation-approval-buttons-box">
                        <Button
                            className="decline-button vacation-approval-cancel-button"
                            onClick={handleCloseApproveVacationsModal}
                        >
                            <CancelIcon style={{ marginRight: 4 }} />
                            {t("global.cancel")}
                        </Button>

                        <Box className="vacation-approval-buttons-row">
                            <Button
                                variant="contained"
                                className="decline-button vacation-approval-buttons-margin"
                                onClick={() => handleAction('decline')}
                                disabled={selectedVacations?.length === 0}
                            >
                                <CancelIcon style={{ marginRight: 4 }} />
                                {t("calendar.vacation_decline")}
                            </Button>
                            <Button
                                variant="contained"
                                className="accept-button vacation-approval-buttons-margin"
                                onClick={() => handleAction('approve')}
                                disabled={selectedVacations?.length === 0}
                            >
                                <CheckCircleIcon style={{ marginRight: 4 }} />
                                {t("calendar.vacation_approve")}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* Refute Modal */}
            <Modal open={openModal} onClose={handleCloseModal}>
                <Box className="vacation-calendar-refute-modal" >
                    <Typography variant="h6" className="vacation-calendar-refute-title">{t("calendar.vacation_refute")}</Typography>
                    <TextField
                        label={t("calendar.vacation_refute_reason")}
                        multiline
                        fullWidth
                        value={changeReason}
                        onChange={(e) => setChangeReason(e.target.value)}
                        rows={4}
                        variant="outlined"
                        InputLabelProps={{
                            className: "vacation-calendar-input-title-color"
                        }}
                        InputProps={{
                            className: "vacation-calendar-input-color"
                        }}
                        className="vacation-calendar-refute-input"
                    />
                    <Box className="buttons-flex vacation-calendar-refute-buttons">
                        <Button
                            variant="outlined"
                            className="decline-button"
                            onClick={handleCloseModal}
                        >
                            <CancelIcon style={{ marginRight: 4 }} />
                            {t("global.cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            className="accept-button"
                            onClick={handleSuggestChanges}
                        >
                            <CheckCircleIcon style={{ marginRight: 4 }} />
                            {t("calendar.vacation_submit")}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
    };

    export default VacationApprovalModal;
